import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  LayoutDashboard, Users, Calendar, FileText, DollarSign,
  BarChart3, Settings, LogOut, Menu, X, Sun, Moon,
  ChevronDown, Bell, Building2, Shield, ShieldAlert, UserCog, Award, GitBranch, FolderOpen, Megaphone, UserPlus, BookOpen, ClipboardList, TrendingUp, HelpCircle, Lock, Loader2, Table2, FileSpreadsheet
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import BirthdayNotification from './BirthdayNotification';
import NotificationBell from './NotificationBell';
import { toast } from 'sonner';

import { API_BASE as API_URL } from '../lib/apiBase';
import { safeJson } from '../lib/safeJson';
import { getActiveEntityId, setActiveEntityId } from '../lib/entityContext';

// Maps each sidebar path to the enabled_modules key the institute must have ticked.
// Items NOT in this map are always shown per-institute (Dashboard, Employees, Settings, etc.).
const PATH_TO_MODULE_KEY = {
  '/attendance': 'attendance',
  '/leaves': 'leaves',
  '/payroll': 'payroll',
  '/recruitment': 'recruitment',
  '/performance': 'performance',
  '/learning-development': 'lnd',
  '/documents': 'documents',
  '/notices': 'notices',
  '/reports': 'reports',
  '/ai-report': 'ai_report',
  '/audit-trail': 'audit',
};

// Separate map for PER-ROLE module hiding (must mirror backend HIDEABLE_MODULES keys).
const PATH_TO_HIDEABLE_KEY = {
  '/branches': 'branches',
  '/employees': 'employees',
  '/attendance': 'attendance',
  '/leaves': 'leaves',
  '/payroll': 'payroll',
  '/recruitment': 'recruitment',
  '/performance': 'performance',
  '/learning-development': 'lnd',
  '/documents': 'documents',
  '/notices': 'notices',
  '/reports': 'reports',
  '/ai-report': 'reports',
  '/salary-revisions': 'salary_revisions',
  '/leave-policy': 'leave_policy',
  '/users': 'users',
};

// Resolve the hideable module key a path belongs to (handles nested paths like /payroll/123).
const hideableKeyForPath = (pathname) => {
  if (PATH_TO_HIDEABLE_KEY[pathname]) return PATH_TO_HIDEABLE_KEY[pathname];
  const hit = Object.keys(PATH_TO_HIDEABLE_KEY).find((p) => pathname.startsWith(p + '/'));
  return hit ? PATH_TO_HIDEABLE_KEY[hit] : null;
};

const getNavItems = (userRole, userPerms = [], institute = null, hiddenModules = []) => {
  const perms = new Set(userPerms || []);
  const has = (p) => perms.has(p);
  const isRole = (...roles) => roles.includes(userRole);
  const baseItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  ];

  // Institute & Branches — second position right after Dashboard for Super Admin / admin / branch_admin
  if (userRole === 'super_admin' || isRole('admin', 'hr', 'branch_admin') || has('branches.view')) {
    baseItems.push({ path: '/branches', label: 'Institute & Branches', icon: GitBranch });
  }

  baseItems.push(
    { path: '/employees', label: 'Employees', icon: Users },
    { path: '/attendance', label: 'Attendance', icon: Calendar },
    { path: '/leaves', label: 'Leaves', icon: FileText },
    { path: '/payroll', label: 'Payroll', icon: DollarSign },
    { path: '/performance', label: 'Performance', icon: Award },
    { path: '/documents', label: 'Documents', icon: FolderOpen },
    { path: '/notices', label: 'Notices', icon: Megaphone },
    { path: '/learning-development', label: 'L&D', icon: BookOpen },
  );

  // Reports (merged with AI Report — both are accessed inside /reports as tabs)
  if (!isRole('employee') || has('reports.view') || has('ai_report.view')) {
    baseItems.push({ path: '/reports', label: 'Reports & AI', icon: BarChart3 });
  }

  // User management — folded into Admin Panel for super_admin. For finance/HR, keep the standalone page.
  if (isRole('admin', 'hr', 'finance') || has('users.view')) {
    if (userRole !== 'super_admin') {
      baseItems.push({ path: '/users', label: 'Users', icon: UserCog });
    }
  }

  // Leave Policy for HR, Institute Admin and Super Admin
  if (isRole('super_admin', 'admin', 'hr', 'branch_admin', 'institute_admin') || has('leave_policy.view')) {
    baseItems.push({ path: '/leave-policy', label: 'Leave Policy', icon: Shield });
  }

  // Department Mapping — unified employee↔department↔HOD editor
  if (isRole('super_admin', 'admin', 'hr', 'institute_admin', 'branch_admin')
      || has('employees.edit') || has('departments.manage')) {
    baseItems.push({ path: '/department-mapping', label: 'Department Mapping', icon: Building2 });
  }

  // Recruitment
  if (isRole('super_admin', 'admin', 'hr', 'hod', 'principal') || has('recruitment.view')) {
    baseItems.push({ path: '/recruitment', label: 'Recruitment', icon: UserPlus });
  }

  // Salary Revisions
  if (isRole('super_admin', 'admin', 'hr', 'finance', 'principal') || has('salary_revision.view')) {
    baseItems.push({ path: '/salary-revisions', label: 'Salary Revisions', icon: TrendingUp });
  }

  // Salary Sheet Templates (custom per-appointment-type salary sheet layouts)
  if (isRole('super_admin', 'admin', 'hr', 'finance', 'finance_head', 'branch_admin', 'institute_admin')) {
    baseItems.push({ path: '/excel-salary-templates', label: 'Salary Templates', icon: FileSpreadsheet });
  }

  // Super Admin Panel — now also contains Roles, Data Integrity (moved out of sidebar)
  if (userRole === 'super_admin') {
    baseItems.push({ path: '/admin-panel', label: 'Admin Panel', icon: Shield });
  }

  // My comprehensive report — visible to every authenticated employee
  baseItems.push({ path: '/my-report', label: 'My Report', icon: FileText });

  // Settings — contains Approval Flow + Audit Trail (moved out of sidebar)
  baseItems.push({ path: '/user-manual', label: 'Help & Guide', icon: HelpCircle });
  baseItems.push({ path: '/settings', label: 'Settings', icon: Settings });

  // Filter by institute enabled_modules — only if the institute has explicitly defined them.
  // Super Admin and users without an entity (Chairman / Trustee / cross-org viewers) are never filtered.
  const enabled = institute && Array.isArray(institute.enabled_modules) ? institute.enabled_modules : null;
  let items = baseItems;
  if (userRole !== 'super_admin' && enabled && enabled.length > 0) {
    items = items.filter((item) => {
      const requiredModule = PATH_TO_MODULE_KEY[item.path];
      if (!requiredModule) return true; // always show items not tied to a toggle-able module
      return enabled.includes(requiredModule);
    });
  }
  // Per-role module hiding (admin-defined in Role Management). Super Admin is never restricted.
  const hidden = new Set(hiddenModules || []);
  if (userRole !== 'super_admin' && hidden.size > 0) {
    items = items.filter((item) => {
      const key = PATH_TO_HIDEABLE_KEY[item.path];
      return !(key && hidden.has(key));
    });
  }
  return items;
};

function MobileBottomNav({ userRole }) {
  const location = useLocation();
  const currentPath = location.pathname;

  const navItems = [
    { path: '/dashboard', label: 'Home', icon: LayoutDashboard },
    { path: '/attendance', label: 'Attendance', icon: Calendar },
    { path: '/leaves', label: 'Leaves', icon: FileText },
    { path: '/notices', label: 'Notices', icon: Bell },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-[9999] bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 md:hidden shadow-[0_-2px_10px_rgba(0,0,0,0.06)] pb-[env(safe-area-inset-bottom)]" data-testid="mobile-bottom-nav">
      <div className="flex items-center justify-around h-16 px-2">
        {navItems.map(item => {
          const isActive = currentPath === item.path || (item.path !== '/dashboard' && currentPath.startsWith(item.path));
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className="flex flex-col items-center justify-center gap-0.5 flex-1 h-full"
              data-testid={`bottom-nav-${item.label.toLowerCase()}`}
            >
              <div className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${isActive ? 'bg-[#DC2626]/10' : ''}`}>
                <item.icon className={`w-5 h-5 transition-colors ${isActive ? 'text-[#DC2626]' : 'text-slate-400'}`} />
              </div>
              <span className={`text-[10px] font-medium transition-colors ${isActive ? 'text-[#DC2626]' : 'text-slate-400'}`}>
                {item.label}
              </span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}

export default function Layout() {
  const { user, logout, setUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: '', newPwd: '', confirm: '' });
  const [pwdSaving, setPwdSaving] = useState(false);

  useEffect(() => {
    fetchEntities();
  }, []);

  // Per-role module hiding: block direct URL access to a module hidden for this user's role.
  useEffect(() => {
    if (!user || user.role === 'super_admin') return;
    const hidden = new Set(user.hidden_modules || []);
    if (hidden.size === 0) return;
    const key = hideableKeyForPath(routeLocation.pathname);
    if (key && hidden.has(key)) {
      toast.error('This section is not available for your role.');
      navigate('/dashboard', { replace: true });
    }
  }, [routeLocation.pathname, user, navigate]);

  // Check if user must change password
  useEffect(() => {
    if (user?.must_change_password) {
      setShowPasswordChange(true);
    }
  }, [user]);

  const handleChangePassword = async () => {
    if (!pwdForm.current || !pwdForm.newPwd) { toast.error('Please fill all fields'); return; }
    if (pwdForm.newPwd.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (pwdForm.newPwd !== pwdForm.confirm) { toast.error('Passwords do not match'); return; }
    if (pwdForm.newPwd === pwdForm.current) { toast.error('New password must be different from current password'); return; }
    setPwdSaving(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({ current_password: pwdForm.current, new_password: pwdForm.newPwd }),
        credentials: 'include',
      });
      const data = await safeJson(res);
      if (!res.ok) {
        // Normalise FastAPI's occasional array-shaped detail so we never render [object]
        let detail = data.detail;
        if (Array.isArray(detail)) detail = detail.map(d => (d && d.msg) ? d.msg : JSON.stringify(d)).join(' ');
        throw new Error(detail || data.message || `Failed (HTTP ${res.status})`);
      }
      toast.success('Password changed successfully!');
      setShowPasswordChange(false);
      setPwdForm({ current: '', newPwd: '', confirm: '' });
      if (user) setUser({ ...user, must_change_password: false });
    } catch (e) { toast.error(e.message); }
    finally { setPwdSaving(false); }
  };

  const fetchEntities = async () => {
    try {
      const token = localStorage.getItem('hrms-token');
      const response = await fetch(`${API_URL}/api/entities`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (response.ok) {
        const raw = await response.json();
        // Sort by hierarchy: Trust (L0) first, then institutes, then branches (alphabetic within level)
        const data = [...raw].sort((a, b) => {
          const la = a.level ?? 0, lb = b.level ?? 0;
          if (la !== lb) return la - lb;
          return (a.name || '').localeCompare(b.name || '');
        });
        setEntities(data);
        const stored = getActiveEntityId();
        const storedEntity = stored ? data.find(e => e.entity_id === stored) : null;
        if (storedEntity) {
          setSelectedEntity(storedEntity);
        } else if (data.length > 0 && !selectedEntity && user?.role !== 'super_admin') {
          setSelectedEntity(data[0]);
          setActiveEntityId(data.length > 1 ? data[0].entity_id : '');
        }
      }
    } catch (e) {
      console.error('Failed to fetch entities:', e);
    }
  };

  const handleSelectEntity = (entity) => {
    const isSwitch = user?.role !== 'super_admin' && entity?.entity_id !== selectedEntity?.entity_id;
    setActiveEntityId(entity?.entity_id || '');
    setSelectedEntity(entity);
    // Non-super-admin data is server-scoped to the acting institute — reload so every
    // module refetches under the newly selected institute.
    if (isSwitch) window.location.reload();
  };

  const handleLogout = async () => {    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex">
      {/* Sidebar */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-slate-900 text-white transform transition-transform duration-300 ease-in-out ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } ${sidebarOpen ? 'lg:w-64' : 'lg:w-20'}`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-4 border-b border-slate-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-md flex items-center justify-center p-1 shrink-0">
                <img src="/sgi-logo.jpg" alt="SGI" className="w-full h-full object-contain" />
              </div>
              {sidebarOpen && (
                <div>
                  <h1 className="font-bold text-lg tracking-tight">SGI HRMS</h1>
                  <p className="text-xs text-slate-400">Campus Management</p>
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-white"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="close-sidebar-btn"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Entity Selector */}
          {sidebarOpen && entities.length > 0 && (
            <div className="p-4 border-b border-slate-800">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-full justify-between bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
                    data-testid="entity-selector"
                  >
                    <span className="truncate">{selectedEntity?.name || 'All Institutes'}</span>
                    <ChevronDown className="w-4 h-4 ml-2 shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56">
                  {user?.role === 'super_admin' && (
                    <DropdownMenuItem
                      onClick={() => { setActiveEntityId(''); setSelectedEntity(null); }}
                      data-testid="entity-option-all"
                    >
                      <div>
                        <p className="font-medium">All Institutes</p>
                        <p className="text-xs text-slate-500">Cumulative view across all entities</p>
                      </div>
                    </DropdownMenuItem>
                  )}
                  {entities.map(entity => (
                    <DropdownMenuItem
                      key={entity.entity_id}
                      onClick={() => handleSelectEntity(entity)}
                      data-testid={`entity-option-${entity.entity_id}`}
                    >
                      <div>
                        <p className="font-medium">{entity.name}</p>
                        <p className="text-xs text-slate-500">{entity.type}</p>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4 custom-scrollbar">
            {getNavItems(user?.role, user?.permissions, entities.find((e) => e.entity_id === user?.entity_id), user?.hidden_modules).map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `sidebar-item mx-2 mb-1 ${isActive ? 'active' : ''}`
                }
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5 shrink-0" />
                {sidebarOpen && <span>{item.label}</span>}
              </NavLink>
            ))}
          </nav>

          {/* Toggle Sidebar */}
          <div className="p-4 border-t border-slate-800 hidden lg:block">
            <Button
              variant="ghost"
              className="w-full text-slate-300 hover:text-white hover:bg-slate-800"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="toggle-sidebar-btn"
            >
              <Menu className="w-5 h-5" />
              {sidebarOpen && <span className="ml-2">Collapse</span>}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-3 md:px-4 lg:px-8 py-3 md:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setMobileMenuOpen(true)}
                data-testid="open-sidebar-btn"
              >
                <Menu className="w-5 h-5" />
              </Button>
              <div className="hidden md:block">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Welcome back, {user?.name?.split(' ')[0] || 'User'}
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
              {/* Mobile: app-like compact title */}
              <div className="md:hidden flex items-center gap-2">
                <div className="w-8 h-8 bg-[#DC2626] rounded-md flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-slate-900 dark:text-white leading-tight">SGI HRMS</h2>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-tight">Hi, {user?.name?.split(' ')[0] || 'User'}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-3">
              {/* Academic Year badge */}
              <AcademicYearBadge />

              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="h-9 w-9"
                data-testid="theme-toggle-btn"
              >
                {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </Button>

              {/* Notifications */}
              <NotificationBell />

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={user?.picture} />
                      <AvatarFallback className="bg-[#DC2626] text-white text-sm">
                        {getInitials(user?.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden md:inline text-sm font-medium">{user?.name}</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                    <p className="text-xs text-slate-400 capitalize mt-1">Role: {user?.role?.replace('_', ' ')}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 min-w-0 max-w-full p-3 md:p-4 lg:p-8 overflow-x-hidden overflow-y-auto pb-bottom-nav">
          <Outlet context={{ selectedEntity, entities, setSelectedEntity }} />
        </main>
      </div>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Birthday Notification */}
      <BirthdayNotification />

      {/* Mobile Bottom Navigation Bar */}
      <MobileBottomNav userRole={user?.role} />

      {/* Force Change Password Dialog */}
      <Dialog open={showPasswordChange} onOpenChange={(v) => { if (!user?.must_change_password) setShowPasswordChange(v); }}>
        <DialogContent className="max-w-md" onInteractOutside={(e) => { if (user?.must_change_password) e.preventDefault(); }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Lock className="w-5 h-5 text-[#DC2626]" /> Change Your Password</DialogTitle>
            <DialogDescription>
              {user?.must_change_password
                ? 'You are using a default password. Please change it now to secure your account.'
                : 'Update your password for security.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Current Password *</Label>
              <Input type="password" value={pwdForm.current} onChange={e => setPwdForm(p => ({ ...p, current: e.target.value }))}
                placeholder="Enter current password" data-testid="pwd-current" />
            </div>
            <div>
              <Label className="text-xs">New Password *</Label>
              <Input type="password" value={pwdForm.newPwd} onChange={e => setPwdForm(p => ({ ...p, newPwd: e.target.value }))}
                placeholder="Min 6 characters" data-testid="pwd-new" />
            </div>
            <div>
              <Label className="text-xs">Confirm New Password *</Label>
              <Input type="password" value={pwdForm.confirm} onChange={e => setPwdForm(p => ({ ...p, confirm: e.target.value }))}
                placeholder="Re-enter new password" data-testid="pwd-confirm" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            {!user?.must_change_password && <Button variant="outline" onClick={() => setShowPasswordChange(false)}>Cancel</Button>}
            <Button onClick={handleChangePassword} disabled={pwdSaving} className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" data-testid="save-password-btn">
              {pwdSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
              Change Password
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Small badge in topbar displaying the current Academic Year (set by Super Admin).
function AcademicYearBadge() {
  const [label, setLabel] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem('hrms-token');
    if (!token) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/system-settings/academic-year/current`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.label) setLabel(d.label); })
      .catch(() => {});
  }, []);
  if (!label) return null;
  return (
    <div
      className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-900 text-xs font-semibold text-indigo-700 dark:text-indigo-300"
      title="Current Academic Year"
      data-testid="ay-badge"
    >
      <Calendar className="w-3 h-3" />
      AY {label}
    </div>
  );
}
